/**
 * <HandoverShiftDrawer> — hand over the shift atomically to the incoming teammate.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  OperationalDrawer, BottomActionBar, OperationalCard,
} from "@/components/operational";
import { ArrowRightLeft } from "lucide-react";
import { foApi, money , formatApiError } from "../foApi";
import { NumberInput } from "@/components/ui/number-input";

export default function HandoverShiftDrawer({ open, onOpenChange, shift, hotelId, onSuccess }) {
  const [teammates, setTeammates] = useState([]);
  const [to, setTo] = useState("");
  const [counted, setCounted] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(""); setCounted("");
    foApi.shiftTeammates({ hotel_id: hotelId }).then(r => setTeammates(r.items || [])).catch(() => setTeammates([]));
  }, [open, hotelId]);

  const submit = async () => {
    const minor = Math.round(parseFloat(counted || 0) * 100);
    if (!to) { toast.error("Pick the incoming teammate"); return; }
    if (Number.isNaN(minor) || minor < 0) { toast.error("Enter counted float"); return; }
    setSaving(true);
    try {
      await foApi.handoverShift(shift.id, {
        to_team_member_id: to,
        counted_float_minor: minor,
      });
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e, "Handover failed"));
    } finally { setSaving(false); }
  };

  return (
    <OperationalDrawer open={open} onOpenChange={onOpenChange}
      title="Handover Shift" subtitle="Atomically close and open a new shift"
      size="sm" testid="handover-shift-drawer"
      footer={
        <BottomActionBar
          primary={{ label: saving ? "Handing over..." : "Confirm Handover",
                       icon: ArrowRightLeft, tone: "primary", onClick: submit,
                       loading: saving, disabled: !to || counted === "",
                       testid: "handover-shift-confirm" }}
          secondaryActions={[
            { label: "Cancel", tone: "secondary", onClick: () => onOpenChange(false),
                testid: "handover-shift-cancel" }
          ]}
        />
      }>
      <div className="p-4 space-y-4">
        <OperationalCard tone="info">
          <div className="text-[12px] text-slate-700">
            Handover will close your shift and open a new one for the incoming
            teammate with the counted float you enter below. Both shifts share
            the same station.
          </div>
        </OperationalCard>
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Incoming teammate</label>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="handover-teammates">
            {teammates.length === 0 ? (
              <input type="text" value={to} onChange={e => setTo(e.target.value)}
                       className="w-full border-2 border-slate-300 rounded-lg p-3 text-[14px] h-12"
                       placeholder="Teammate ID"
                       data-testid="handover-to-manual"/>
            ) : teammates.slice(0, 15).map(t => (
              <button key={t.id} onClick={() => setTo(t.id)}
                className={`min-h-[44px] px-3 rounded-lg border-2 text-[13px] font-medium ${to === t.id ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white border-slate-300"}`}
                data-testid={`handover-to-${t.id}`}>
                {t.name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Counted cash to hand over (₹)</label>
          <NumberInput min={0} step="0.01" value={counted}
                   onChange={e => setCounted(e.target.value)}
                   className="w-full mt-1 border-2 border-slate-300 rounded-lg p-3 text-[18px] font-semibold h-14"
                   placeholder="0.00"
                   data-testid="handover-counted"/>
        </div>
      </div>
    </OperationalDrawer>
  );
}
